import { add, isNegative, subtract } from '@aeris/money';
/**
 * The rules that govern a movement.
 *
 * One file, so that "can stock go negative?" has exactly one answer and one
 * place to change it.
 */
/**
 * Whether an exit may take a balance below zero.
 *
 * **This is the one place this is decided.** It is a named constant rather
 * than a check constraint in the database or an `if` inside the repository,
 * because it is genuinely a business policy and not a data-integrity rule:
 *
 *   - a shop that scans everything at the counter wants `false`. A till that
 *     sells the eleventh of ten items is telling its owner that the count is
 *     wrong, and finding that out at the moment of sale is the point.
 *
 *   - a distributor whose goods-received paperwork lags behind the physical
 *     pallet wants `true`. Refusing the sale does not make the pallet less
 *     present; it just stops the business while somebody types.
 *
 * Both are legitimate, and desde a F103 a loja escolhe: a resposta mora em
 * `company.allow_negative_stock` e chega aqui pelo parâmetro `allowNegative`,
 * que as rotas leem e passam.
 *
 * ## O que esta constante é hoje
 *
 * **Já não é a fonte da decisão — é só o padrão seguro do parâmetro.** Ela
 * continua existindo para que um chamador que não sabe da política (um teste,
 * uma rota antiga) recuse em vez de permitir.
 *
 * O comentário anterior prometia: *"quando aquele módulo chegar, `false` aqui
 * vira uma consulta e todo call site já passa por `checkMovement`"*. Chegou, e
 * a previsão estava certa — nenhum call site mudou de forma. Um comentário que
 * descreve um futuro já acontecido é a próxima pessoa acreditando na coisa
 * errada, então ele foi reescrito em vez de mantido.
 *
 * The safe default is chosen deliberately: a business that needs negative
 * stock will say so loudly on day one, whereas a business that has silently
 * been going negative discovers it during an audit.
 */
export const ALLOW_NEGATIVE_STOCK = false;
/**
 * Applies a movement to a balance and says whether it is allowed.
 *
 * Pure, so the rule can be tested without a database — and so the repository,
 * which has already taken a row lock by the time it calls this, has nothing to
 * decide for itself.
 *
 * The comparison is against `onHand` rather than `available`. Reserved stock is
 * committed, not gone, and until sales exist `reserved` is zero everywhere so
 * the two are identical. When reservations arrive, whether a manual exit may
 * eat into reserved stock is a decision that belongs to that feature; leaving
 * it as `onHand` now is the reading that does not silently pre-empt it.
 */
export const checkMovement = (onHand, direction, quantity, allowNegative = ALLOW_NEGATIVE_STOCK) => {
    const next = direction === 'in' ? add(onHand, quantity) : subtract(onHand, quantity);
    if (!allowNegative && isNegative(next)) {
        return {
            ok: false,
            reason: 'insufficient-stock',
            available: onHand,
            requested: quantity,
        };
    }
    return { ok: true, onHand: next };
};
/**
 * Applies a reservation to a balance and says whether it is allowed.
 *
 * Compared against `available` — `onHand - reserved` — and not against
 * `onHand`, which is the difference between a reservation and a movement.
 * Reserving the same ten units for two customers is exactly the failure the
 * column exists to prevent, and comparing against `onHand` would permit it
 * while looking correct.
 *
 * `allowNegative` follows the same policy constant as a movement, so a
 * distributor that permits negative stock also permits over-reservation. That
 * is the consistent reading: a business that will sell what it has not received
 * will also promise it.
 */
export const checkReservation = (onHand, reserved, quantity, allowNegative = ALLOW_NEGATIVE_STOCK) => {
    const available = subtract(onHand, reserved);
    const remaining = subtract(available, quantity);
    if (!allowNegative && isNegative(remaining)) {
        return {
            ok: false,
            reason: 'insufficient-stock',
            available,
            requested: quantity,
        };
    }
    return { ok: true, reserved: add(reserved, quantity) };
};
/**
 * Gives a reservation back.
 *
 * Clamped at zero rather than allowed to go negative. A negative reservation is
 * nonsense in every reading, the check constraint in migration 0007 refuses it,
 * and the realistic cause of an over-release — a document released twice by a
 * retry — should leave the figure correct rather than turn a retry into a
 * failed write.
 */
export const releaseReservation = (reserved, quantity) => {
    const remaining = subtract(reserved, quantity);
    return isNegative(remaining)
        ? { units: 0n, scale: remaining.scale }
        : remaining;
};
//# sourceMappingURL=rules.js.map